import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { auth } from '../../../firebase/config';
import { getSiteSettings, updateSiteSettings } from '../../../services/settingsService';
import { logout } from '../../../services/authService';
import { useAuth } from '../../../context/AuthContext';
import ConfirmModal from '../../../components/common/ConfirmModal';

const SERVICE_CATEGORIES = [
  { key: 'moving', label: '이사' },
  { key: 'cleaning', label: '입주청소' },
  { key: 'internet_tv', label: '인터넷/TV' },
  { key: 'interior', label: '인테리어' },
  { key: 'aircon', label: '에어컨' },
  { key: 'water_purifier', label: '정수기' },
  { key: 'kiosk', label: '키오스크' },
];

const ServiceSettings = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();

  const [services, setServices] = useState(
    SERVICE_CATEGORIES.reduce((acc, c) => ({ ...acc, [c.key]: true }), {}),
  );
  const [notif, setNotif] = useState({
    main: true, budget: true, all: true,
    // 채널별 세분화 (2026-04-23 확장)
    channel_alimtalk: true,   // 카카오 알림톡
    channel_sms: true,        // SMS
    channel_push: true,       // FCM 푸시
    channel_email: false,     // 이메일
  });
  const [contactEmail, setContactEmail] = useState('');
  const [partnerEmail, setPartnerEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwError, setPwError] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    getSiteSettings().then((s) => {
      if (s.activeServices) setServices((prev) => ({ ...prev, ...s.activeServices }));
      if (s.notif) setNotif((prev) => ({ ...prev, ...s.notif }));
      if (s.contactEmail) setContactEmail(s.contactEmail);
      if (s.partnerEmail) setPartnerEmail(s.partnerEmail);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (userData) {
      setName(userData.name || '');
      setPhone(userData.phone || '');
    }
  }, [userData]);

  const toggleService = (key) => setServices({ ...services, [key]: !services[key] });
  const toggleNotif = (key) => setNotif({ ...notif, [key]: !notif[key] });

  const saveSettings = async () => {
    try {
      await updateSiteSettings({
        activeServices: services,
        notif,
        contactEmail,
        partnerEmail,
      });
      alert('설정이 저장되었습니다.');
    } catch (err) {
      alert('저장 실패: ' + err.message);
    }
  };

  const handleChangePassword = async () => {
    setPwError('');
    if (!pwForm.current || !pwForm.next || !pwForm.confirm) {
      setPwError('모든 항목을 입력해 주세요.');
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      setPwError('새 비밀번호가 일치하지 않습니다.');
      return;
    }
    if (pwForm.next.length < 6) {
      setPwError('비밀번호는 6자 이상이어야 합니다.');
      return;
    }
    const current = auth.currentUser;
    if (!current) {
      setPwError('로그인 상태가 아닙니다.');
      return;
    }
    setPwSaving(true);
    try {
      const cred = EmailAuthProvider.credential(current.email, pwForm.current);
      await reauthenticateWithCredential(current, cred);
      await updatePassword(current, pwForm.next);
      setPwForm({ current: '', next: '', confirm: '' });
      alert('비밀번호가 변경되었습니다.');
    } catch (err) {
      setPwError(err.code === 'auth/wrong-password' ? '현재 비밀번호가 올바르지 않습니다.' : (err.message || '변경 실패'));
    } finally {
      setPwSaving(false);
    }
  };

  const [logoutOpen, setLogoutOpen] = useState(false);
  const handleLogout = () => setLogoutOpen(true);
  const confirmLogout = async () => {
    setLogoutOpen(false);
    try {
      await logout();
      navigate('/admin/login', { replace: true });
    } catch {
      navigate('/admin/login', { replace: true });
    }
  };

  return (
    <Container>
      <Title>설정</Title>

      <Card>
        <CardTitle>계정 정보</CardTitle>
        <FieldRow>
          <Field><Label>이메일</Label><Input value={userData?.email || ''} readOnly /></Field>
          <Field><Label>이름</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
          <Field><Label>연락처</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="010-0000-0000" /></Field>
        </FieldRow>
      </Card>

      <Card>
        <CardTitle>비밀번호 변경</CardTitle>
        <FieldRow>
          <Field><Label>현재 비밀번호</Label><Input type="password" value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} /></Field>
          <Field><Label>새 비밀번호</Label><Input type="password" value={pwForm.next} onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })} /></Field>
          <Field><Label>비밀번호 확인</Label><Input type="password" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} /></Field>
        </FieldRow>
        {pwError && <ErrorToast>{pwError}</ErrorToast>}
        <CardFooter>
          <PrimaryBtn disabled={pwSaving} onClick={handleChangePassword}>{pwSaving ? '변경 중...' : '비밀번호 변경'}</PrimaryBtn>
        </CardFooter>
      </Card>

      <Card>
        <CardTitle>DB 가격 / 수수료 설정</CardTitle>
        <InlineLine>
          <span>서비스별 DB 가격 및 수수료 정책은 별도 페이지에서 관리합니다.</span>
          <TextBtn onClick={() => navigate('/admin/settings/pricing')}>바로가기</TextBtn>
        </InlineLine>
      </Card>

      <Card>
        <CardTitle>이메일 설정</CardTitle>
        <FieldRow>
          <Field><Label>고객센터 이메일</Label><Input value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="support@ieum.in" /></Field>
          <Field><Label>제휴업체 이메일</Label><Input value={partnerEmail} onChange={(e) => setPartnerEmail(e.target.value)} placeholder="partner@ieum.in" /></Field>
        </FieldRow>
      </Card>

      <Card>
        <CardTitle>약관 및 정책</CardTitle>
        <InlineLine>
          <span>서비스 이용약관, 개인정보 처리방침, 제3자 제공 동의 등을 관리합니다.</span>
          <TextBtn onClick={() => navigate('/admin/settings/terms')}>바로가기</TextBtn>
        </InlineLine>
      </Card>

      <Card>
        <CardTitle>서비스 카테고리</CardTitle>
        <CardDesc>활성화된 카테고리만 고객 신청 폼에 노출됩니다.</CardDesc>
        <CategoryGrid>
          {SERVICE_CATEGORIES.map((cat) => (
            <CategoryBox key={cat.key} $on={services[cat.key]} onClick={() => toggleService(cat.key)}>
              <CatLabel>{cat.label}</CatLabel>
              <CatState>{services[cat.key] ? '사용' : '중지'}</CatState>
            </CategoryBox>
          ))}
        </CategoryGrid>
      </Card>

      <Card>
        <CardTitle>알림 설정</CardTitle>
        <ToggleList>
          <ToggleRow>
            <ToggleInfo>
              <ToggleLabel>주요 시스템 알림</ToggleLabel>
              <ToggleDesc>신규 가입, 결제 실패 등 핵심 이벤트 알림</ToggleDesc>
            </ToggleInfo>
            <Toggle>
              <ToggleInput type="checkbox" checked={notif.main} onChange={() => toggleNotif('main')} />
              <ToggleSlider />
            </Toggle>
          </ToggleRow>
          <ToggleRow>
            <ToggleInfo>
              <ToggleLabel>예산 알림</ToggleLabel>
              <ToggleDesc>미수금, 정산 임계치 초과 시 알림</ToggleDesc>
            </ToggleInfo>
            <Toggle>
              <ToggleInput type="checkbox" checked={notif.budget} onChange={() => toggleNotif('budget')} />
              <ToggleSlider />
            </Toggle>
          </ToggleRow>
          <ToggleRow>
            <ToggleInfo>
              <ToggleLabel>고객 모든 알림</ToggleLabel>
              <ToggleDesc>신규 고객 접수 및 민원 모든 알림</ToggleDesc>
            </ToggleInfo>
            <Toggle>
              <ToggleInput type="checkbox" checked={notif.all} onChange={() => toggleNotif('all')} />
              <ToggleSlider />
            </Toggle>
          </ToggleRow>
        </ToggleList>

        <ChannelTitle>발송 채널</ChannelTitle>
        <ToggleList>
          <ToggleRow>
            <ToggleInfo>
              <ToggleLabel>카카오 알림톡</ToggleLabel>
              <ToggleDesc>솔라피 알림톡 채널 (템플릿 기반)</ToggleDesc>
            </ToggleInfo>
            <Toggle>
              <ToggleInput type="checkbox" checked={notif.channel_alimtalk} onChange={() => toggleNotif('channel_alimtalk')} />
              <ToggleSlider />
            </Toggle>
          </ToggleRow>
          <ToggleRow>
            <ToggleInfo>
              <ToggleLabel>SMS</ToggleLabel>
              <ToggleDesc>알림톡 발송 실패 시 SMS 대체 발송</ToggleDesc>
            </ToggleInfo>
            <Toggle>
              <ToggleInput type="checkbox" checked={notif.channel_sms} onChange={() => toggleNotif('channel_sms')} />
              <ToggleSlider />
            </Toggle>
          </ToggleRow>
          <ToggleRow>
            <ToggleInfo>
              <ToggleLabel>FCM 푸시</ToggleLabel>
              <ToggleDesc>앱/웹 푸시 알림 (fcmTokens 기반)</ToggleDesc>
            </ToggleInfo>
            <Toggle>
              <ToggleInput type="checkbox" checked={notif.channel_push} onChange={() => toggleNotif('channel_push')} />
              <ToggleSlider />
            </Toggle>
          </ToggleRow>
          <ToggleRow>
            <ToggleInfo>
              <ToggleLabel>이메일</ToggleLabel>
              <ToggleDesc>Nodemailer 경유 이메일 발송 (FormMail 설정 사용)</ToggleDesc>
            </ToggleInfo>
            <Toggle>
              <ToggleInput type="checkbox" checked={notif.channel_email} onChange={() => toggleNotif('channel_email')} />
              <ToggleSlider />
            </Toggle>
          </ToggleRow>
        </ToggleList>
      </Card>

      <SaveRow>
        <TextBtn onClick={handleLogout}>로그아웃</TextBtn>
        <PrimaryBtn onClick={saveSettings}>설정 저장</PrimaryBtn>
      </SaveRow>

      {logoutOpen && (
        <ConfirmModal
          type="question"
          title="로그아웃 하시겠습니까?"
          confirmText="로그아웃"
          cancelText="취소"
          onConfirm={confirmLogout}
          onCancel={() => setLogoutOpen(false)}
        />
      )}
    </Container>
  );
};

export default ServiceSettings;

const Container = styled.div``;

const Title = styled.h1`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border-radius: ${({ theme }) => theme.radius.md};
  box-shadow: ${({ theme }) => theme.shadow.sm};
  padding: 20px 24px;
  margin-bottom: 16px;
`;

const CardTitle = styled.h2`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 12px;
  color: ${({ theme }) => theme.colors.text};
`;

const CardDesc = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 16px;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 6px;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  background: ${({ readOnly, theme }) => (readOnly ? theme.colors.borderLight : '#fff')};
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`;

const ErrorToast = styled.div`
  margin-top: 10px;
  padding: 10px 12px;
  background: #FEE2E2;
  color: ${({ theme }) => theme.colors.danger};
  font-size: 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
`;

const InlineLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const TextBtn = styled.button`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.primary};
  background: none;
  padding: 4px 0;
  &:hover:not(:disabled) { text-decoration: underline; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
`;

const CategoryBox = styled.button`
  padding: 14px;
  border: 1px solid ${({ $on, theme }) => ($on ? theme.colors.primary : theme.colors.border)};
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.sm};
  text-align: left;
  transition: all 0.15s;
  &:hover { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const CatLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 4px;
`;

const CatState = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ToggleList = styled.div``;
const ChannelTitle = styled.h3`font-size:13px;font-weight:600;color:${({ theme }) => theme.colors.text};margin:20px 0 12px;padding-top:16px;border-top:1px solid ${({ theme }) => theme.colors.borderLight};`;

const ToggleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  &:last-child { border-bottom: none; }
`;

const ToggleInfo = styled.div``;

const ToggleLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`;

const ToggleDesc = styled.p`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textLight};
  margin-top: 2px;
`;

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;
  flex-shrink: 0;
`;

const ToggleSlider = styled.span`
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: #D1D5DB;
  border-radius: 11px;
  cursor: pointer;
  transition: 0.2s;
  &::before {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    left: 3px;
    bottom: 3px;
    background: #fff;
    border-radius: 50%;
    transition: 0.2s;
  }
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
  &:checked + ${ToggleSlider} {
    background: ${({ theme }) => theme.colors.primary};
  }
  &:checked + ${ToggleSlider}::before {
    transform: translateX(18px);
  }
`;

const SaveRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
`;

const PrimaryBtn = styled.button`
  padding: 10px 26px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  font-weight: 500;
  &:disabled { opacity: 0.6; cursor: default; }
`;
